import React, { FC } from "react"

import { RuleFieldDef } from "services/tags/type"

import { Operator } from "../../services/tags/type"

import { ensureBetweenArray, parseNumberInput } from "./helper"
import { createEmptyCond, createEmptyGroup, isGroup, RuleCond, RuleGroup, RuleNode } from "./types"

const opsLabel: Record<Operator, string> = {
  "=": "=",
  "!=": "!=",
  ">": ">",
  ">=": ">=",
  "<": "<",
  "<=": "<=",
  equals: "is equal to",
  not_equals: "is not equal to",
  contains: "contains",
  between: "between",
  in_list: "in list",
  not_in_list: "not in list",
  is_empty: "is empty",
  is_not_empty: "is not empty"
}
function getGroupMode(g: RuleGroup): "all" | "any" {
  return g.all ? "all" : "any"
}
function getGroupItems(g: RuleGroup): RuleNode[] {
  return g.all ?? g.any ?? []
}
function setGroupItems(g: RuleGroup, mode: "all" | "any", items: RuleNode[]): RuleGroup {
  return mode === "all" ? { all: items } : { any: items }
}

export const RuleBuilder: FC<{
  value: RuleGroup
  fields: RuleFieldDef[]
  onChange: (next: RuleGroup) => void
}> = ({ value, fields, onChange }) => {
  const mode = getGroupMode(value)
  const items = getGroupItems(value)

  const setItems = (nextItems: RuleNode[]) => {
    onChange(setGroupItems(value, mode, nextItems))
  }

  const updateNode = (idx: number, next: RuleNode) => {
    const copy = [...items]
    copy[idx] = next
    setItems(copy)
  }

  const removeNode = (idx: number) => {
    const copy = [...items]
    copy.splice(idx, 1)
    setItems(copy)
  }

  const addCond = () => setItems([...items, createEmptyCond()])
  const addGroup = (m: "all" | "any") => setItems([...items, createEmptyGroup(m)])

  return (
    <div className="border border-[rgba(139,92,246,0.5)] rounded-xl p-4 bg-white dark:bg-[#0C0C1A]">
      <div className="flex items-center gap-3 mb-3">
        <div className="px-3 py-1 rounded-lg bg-[rgba(139,92,246,1)] text-white text-sm font-semibold">{mode === "all" ? "And" : "Or"}</div>

        <select
          className="select select-bordered select-sm"
          value={mode}
          onChange={(e) => {
            const nextMode = e.target.value as "all" | "any"
            onChange(setGroupItems(value, nextMode, items))
          }}
        >
          <option value="all">All conditions (AND)</option>
          <option value="any">Any condition (OR)</option>
        </select>

        <div className="ml-auto flex gap-2">
          <button className="btn btn-outline btn-sm" onClick={addCond}>
            + Condition
          </button>
          <div className="dropdown dropdown-end">
            <label tabIndex={0} className="btn btn-outline btn-sm">
              + Group
            </label>
            <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
              <li>
                <a onClick={() => addGroup("all")}>All (AND) group</a>
              </li>
              <li>
                <a onClick={() => addGroup("any")}>Any (OR) group</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((node, idx) =>
          isGroup(node) ? (
            <div key={idx} className="relative">
              <div className="absolute -left-2 top-2">
                <button className="btn btn-ghost btn-xs text-red-500" onClick={() => removeNode(idx)}>
                  ✕
                </button>
              </div>
              <div className="pl-4">
                <RuleBuilder value={node} fields={fields} onChange={(next) => updateNode(idx, next)} />
              </div>
            </div>
          ) : (
            <ConditionRow
              key={idx}
              cond={node}
              fields={fields}
              onChange={(next) => updateNode(idx, next)}
              onRemove={() => removeNode(idx)}
            />
          )
        )}

        {!items.length && <div className="opacity-60 text-sm">Add a condition or a group to start.</div>}
      </div>
    </div>
  )
}

const ConditionRow: FC<{
  cond: RuleCond
  fields: RuleFieldDef[]
  onChange: (next: RuleCond) => void
  onRemove: () => void
}> = ({ cond, fields, onChange, onRemove }) => {
  const field = fields.find((f) => f.key === cond.field)
  const ops = field?.ops ?? []
  const needsValue = cond.op && !["is_empty", "is_not_empty"].includes(cond.op)
  const isBetween = cond.op === "between"

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...cond, field: e.target.value, op: "", value: "" })
  }

  const handleOpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextOp = e.target.value

    if (nextOp === "between") {
      onChange({ ...cond, op: nextOp, value: ["", ""] })
      return
    }

    const nextValue = cond.op === "between" ? "" : cond.value
    onChange({ ...cond, op: nextOp, value: nextValue })
  }

  const handleEnumChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...cond, value: e.target.value })
  }

  const handleBooleanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value
    onChange({ ...cond, value: v === "" ? "" : v === "true" })
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...cond, value: e.target.value })
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseNumberInput(e.target.value)
    onChange({ ...cond, value: parsed === "" ? "" : parsed })
  }

  const handleBetweenMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [_a, b] = ensureBetweenArray(cond.value)
    const nextA = parseNumberInput(e.target.value)
    onChange({ ...cond, value: [nextA === "" ? "" : nextA, b ?? ""] })
  }

  const handleBetweenMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [a, _b] = ensureBetweenArray(cond.value)
    const nextB = parseNumberInput(e.target.value)
    onChange({ ...cond, value: [a ?? "", nextB === "" ? "" : nextB] })
  }

  const renderValueInput = () => {
    if (!needsValue) {
      return <div className="flex items-center w-full opacity-60 text-sm px-2">No value needed</div>
    }

    if (isBetween) {
      if (field?.type !== "number") {
        return <div className="flex items-center w-full opacity-60 text-sm px-2">Between is only supported for numbers</div>
      }

      const [a, b] = ensureBetweenArray(cond.value)

      return (
        <div className="flex gap-2 w-full">
          <input
            type="number"
            className="input input-bordered w-full"
            value={a ?? ""}
            onChange={handleBetweenMinChange}
            placeholder="Min"
          />
          <input
            type="number"
            className="input input-bordered w-full"
            value={b ?? ""}
            onChange={handleBetweenMaxChange}
            placeholder="Max"
          />
        </div>
      )
    }

    if (field?.type === "enum") {
      return (
        <select className="select select-bordered w-full" value={cond.value ?? ""} onChange={handleEnumChange}>
          <option value="">Select value</option>
          {(field.values ?? []).map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      )
    }

    if (field?.type === "boolean") {
      return (
        <select className="select select-bordered w-full" value={String(cond.value ?? "")} onChange={handleBooleanChange}>
          <option value="">Select value</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )
    }

    if (field?.type === "number") {
      return (
        <input
          type="number"
          className="input input-bordered w-full"
          value={cond.value ?? ""}
          onChange={handleNumberChange}
          placeholder={field?.placeholder ?? "Value"}
        />
      )
    }

    return (
      <input
        className="input input-bordered w-full"
        value={cond.value ?? ""}
        onChange={handleTextChange}
        placeholder={field?.placeholder ?? "Value"}
      />
    )
  }

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-stretch border border-[rgba(139,92,246,0.35)] rounded-xl p-3">
      <button className="btn btn-ghost btn-sm text-red-500 self-start" onClick={onRemove}>
        ✕
      </button>

      <select className="select select-bordered w-full sm:w-[260px]" value={cond.field} onChange={handleFieldChange}>
        <option value="">Select field</option>
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>

      <select className="select select-bordered w-full sm:w-[240px]" value={cond.op} onChange={handleOpChange} disabled={!cond.field}>
        <option value="">Select operator</option>
        {ops.map((op: Operator) => (
          <option key={op} value={op}>
            {opsLabel[op] ?? op}
          </option>
        ))}
      </select>

      {renderValueInput()}
    </div>
  )
}
